/**
 * Platform-agnostic auth core (extracted from `web/src/net/auth.ts` for #104, D-104.12).
 *
 * The client identity + app-owned, in-app registered auth logic (M7.1; FR-2.11,
 * roadmap 43), with the two browser couplings that blocked native reuse lifted behind
 * injected ports so both the web client and the React Native client (#104) run the same
 * core verbatim:
 *
 *   1. **Server base URL is a parameter** (`AuthCoreConfig.baseUrl`, D-104.12a) — never a
 *      top-level `import.meta.env`/`config.ts` read. Web injects its `SERVER_HTTP_URL`
 *      (empty in prod → same-origin relative; localhost in dev); RN injects an explicit
 *      staging base URL.
 *   2. **Guest-identity persistence is behind a storage port** ({@link KeyValueStorage},
 *      D-104.12b) — never a direct `localStorage` reference. Web injects a `localStorage`
 *      adapter; RN injects an `expo-secure-store`-backed adapter (a synchronous in-memory
 *      mirror hydrated at boot — the port stays sync so web behavior is unchanged).
 *
 * The port is deliberately **synchronous** (`getItem`/`setItem`/`removeItem`, the
 * `localStorage` subset this core uses) so `loadIdentity` and friends stay sync exactly
 * as web relies on today; the RN adapter (a later #104 slice) mirrors the async
 * secure-store into memory to satisfy that.
 *
 * Cognito session tokens still live in {@link MemoryStorage} on both platforms and are
 * never persisted (D-43.6, NFR-5.5) — the durable session is the signed guest token in
 * the injected storage port.
 *
 * Everything here is unit-tested against a fake storage port + mocked
 * `amazon-cognito-identity-js` (SRP + emailed codes can't run under CI), matching the
 * original web-side coverage; see `auth-core.test.ts`.
 */

import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type ICognitoStorage,
} from 'amazon-cognito-identity-js';
import type { DateOfBirth } from '@jeopardy/shared';

/**
 * The synchronous key/value persistence port the auth core uses for the durable guest
 * identity (D-104.12b). A subset of the `localStorage` surface; web injects a
 * `localStorage` adapter, RN injects an `expo-secure-store`-backed (memory-mirrored)
 * adapter. Adapters are responsible for degrading safely when their backing store is
 * unavailable (e.g. the web adapter returns `null`/no-ops under SSR, where there is no
 * `localStorage`).
 */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/** Injected configuration for {@link createAuthCore} (D-104.12). */
export interface AuthCoreConfig {
  /**
   * HTTP base for the control-plane endpoints (D-104.12a). Empty string = same-origin
   * (relative) requests; otherwise an absolute origin. Never read from the environment
   * inside this package.
   */
  baseUrl: string;
  /** Durable guest-identity persistence port (D-104.12b). */
  storage: KeyValueStorage;
  /**
   * Returns the anonymous first-party funnel key (#148 D148.4, NFR-6.6) threaded onto
   * the identity/account control-plane POSTs. Injected because the web implementation is
   * `localStorage`-backed; a native client supplies its own (or a no-op returning `''`).
   */
  getFunnelKey: () => string;
}

/**
 * Cognito client parameters for the in-app SRP flow (FR-2.11), fetched at runtime from
 * `GET /api/config` (never baked into the bundle). Only `region`/`userPoolId`/`clientId`
 * are used by the SRP flow; `domain` is legacy Hosted-UI rollback insurance (D-43.4).
 */
export interface CognitoClientConfig {
  /** Legacy Cognito Hosted-UI domain — unused by the in-app SRP flow (FR-2.11). */
  domain?: string;
  /** Public app-client id (SRP flow, no secret). */
  clientId: string;
  region: string;
  userPoolId: string;
}

const PLAYER_ID_KEY = 'jeopardy.playerId';
const GUEST_TOKEN_KEY = 'jeopardy.guestToken';
/** Whether the persisted identity is registered (`'1'`) vs a guest (absent). */
const REGISTERED_KEY = 'jeopardy.registered';
/** The registered display name (Cognito username), if signed in. */
const DISPLAY_NAME_KEY = 'jeopardy.displayName';
/** The registered account's editable, player-chosen display name (FR-2.12), if set. */
const PREFERRED_NAME_KEY = 'jeopardy.preferredName';
/** Whether the registered account has a profile birth year set (`'1'`) — FR-2.8. */
const HAS_BIRTH_YEAR_KEY = 'jeopardy.hasBirthYear';
/** The registered account's profile-picture URL, if set (#42, FR-2.10). */
const AVATAR_URL_KEY = 'jeopardy.avatarUrl';

/** The persisted client identity. */
export interface Identity {
  playerId: string;
  /** Server-signed guest/identity token presented on join (FR-9.3). */
  guestToken: string;
  /** False once the player has upgraded to a registered account (FR-2.2). */
  isGuest: boolean;
  /** Registered display name (Cognito username) — the fallback auto-join player name. */
  displayName?: string;
  /**
   * The registered account's editable, player-chosen display name (FR-2.12), distinct
   * from the login username. When set it is the name the player auto-joins under and is
   * shown to others; when unset the player falls back to {@link displayName} (the
   * username). `undefined` for guests and registered accounts that haven't set one.
   */
  preferredName?: string;
  /**
   * Whether the registered account has a profile birth year set (FR-2.8). Only the
   * BOOLEAN is ever known client-side — the raw year is never sent back. `undefined`
   * for guests (they self-report a band per game instead).
   */
  hasBirthYear?: boolean;
  /**
   * The registered account's profile-picture URL (#42, FR-2.10), a same-origin
   * `/avatars/<key>` path. `undefined` for guests and registered accounts without a
   * picture.
   */
  avatarUrl?: string;
}

// ---------------------------------------------------------------------------
// Pure validators (no config needed) — exported standalone.
// ---------------------------------------------------------------------------

/** True if `year` is a plausible birth year — mirrors the server's `1900..currentYear`. */
export function isPlausibleBirthYear(year: number): boolean {
  return Number.isInteger(year) && year >= 1900 && year <= new Date().getFullYear();
}

/** Max length of an editable display name (FR-2.12) — mirrors the server's bound. */
export const DISPLAY_NAME_MAX_LEN = 30;

/**
 * True if `name` (already trimmed) is a valid editable display name (FR-2.12) — mirrors
 * the server's `normalizePreferredName`: 1–{@link DISPLAY_NAME_MAX_LEN} characters and no
 * control characters. An empty string is NOT valid here; clearing is expressed with
 * `null` in `updatePreferredName`.
 */
export function isValidDisplayName(name: string): boolean {
  if (name.length === 0 || name.length > DISPLAY_NAME_MAX_LEN) return false;
  for (const ch of name) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) return false;
  }
  return true;
}

/**
 * In-memory implementation of the Cognito library's storage port (D-43.6, NFR-5.5).
 * `amazon-cognito-identity-js` otherwise persists the session (access/refresh/ID
 * tokens) to `localStorage`; we only ever use the ID token transiently to post to
 * `/account/claim`, so we back the pool/users with a process-memory store and the
 * tokens never touch disk. The durable session stays the signed guest token.
 */
export class MemoryStorage implements ICognitoStorage {
  private store: Record<string, string> = {};
  getItem(key: string): string | null {
    return this.store[key] ?? null;
  }
  setItem(key: string, value: string): void {
    this.store[key] = value;
  }
  removeItem(key: string): void {
    delete this.store[key];
  }
  clear(): void {
    this.store = {};
  }
}

/** The bound auth-core surface returned by {@link createAuthCore}. */
export interface AuthCore {
  loadIdentity(): Identity | null;
  saveIdentity(id: {
    playerId: string;
    guestToken: string;
    isGuest?: boolean;
    displayName?: string;
    hasBirthYear?: boolean;
    avatarUrl?: string | null;
    preferredName?: string | null;
  }): void;
  savePreferredName(preferredName: string | null): void;
  saveAvatarUrl(avatarUrl: string | null): void;
  saveHasBirthYear(hasBirthYear: boolean): void;
  ensureGuestIdentity(forceFresh?: boolean): Promise<Identity>;
  recoverStaleIdentity(): Promise<Identity>;
  register(
    username: string,
    email: string,
    password: string,
    dateOfBirth: DateOfBirth,
  ): Promise<void>;
  signIn(
    cfg: CognitoClientConfig,
    username: string,
    password: string,
  ): Promise<{ idToken: string }>;
  forgotPassword(cfg: CognitoClientConfig, username: string): Promise<void>;
  confirmReset(
    cfg: CognitoClientConfig,
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void>;
  claimAccount(idToken: string): Promise<Identity>;
  updateAvatar(
    dataBase64: string,
    contentType: string,
    guestToken: string,
  ): Promise<{ avatarUrl: string }>;
  clearAvatar(guestToken: string): Promise<void>;
  updateBirthYear(
    birthYear: number | null,
    guestToken: string,
  ): Promise<{ hasBirthYear: boolean }>;
  updatePreferredName(
    name: string | null,
    guestToken: string,
  ): Promise<{ preferredName?: string }>;
  acceptTerms(
    version: string,
    guestToken: string,
  ): Promise<{ tosAcceptedVersion: string; tosAcceptedEpochMs: number }>;
  clearIdentity(): void;
}

/**
 * Create an auth core bound to an injected {@link AuthCoreConfig} (D-104.12). Each core
 * owns its OWN in-flight-mint dedup ({@link AuthCore.ensureGuestIdentity} #60) + Cognito
 * memory storage, so callers MUST create ONE shared instance per app and reuse it (the
 * web client does this in `web/src/net/auth.ts`); creating multiples would let concurrent
 * first-use mints diverge into competing guest identities.
 */
export function createAuthCore(config: AuthCoreConfig): AuthCore {
  const { baseUrl, storage, getFunnelKey } = config;

  // ---- Guest identity persistence (via the injected storage port) --------

  function loadIdentity(): Identity | null {
    const playerId = storage.getItem(PLAYER_ID_KEY);
    const guestToken = storage.getItem(GUEST_TOKEN_KEY);
    if (!playerId || !guestToken) return null;
    const isGuest = storage.getItem(REGISTERED_KEY) !== '1';
    const displayName = storage.getItem(DISPLAY_NAME_KEY) ?? undefined;
    const avatarUrl = storage.getItem(AVATAR_URL_KEY) ?? undefined;
    const preferredName = storage.getItem(PREFERRED_NAME_KEY) ?? undefined;
    return {
      playerId,
      guestToken,
      isGuest,
      ...(displayName ? { displayName } : {}),
      ...(isGuest
        ? {}
        : {
            hasBirthYear: storage.getItem(HAS_BIRTH_YEAR_KEY) === '1',
            ...(avatarUrl ? { avatarUrl } : {}),
            ...(preferredName ? { preferredName } : {}),
          }),
    };
  }

  function savePreferredName(preferredName: string | null): void {
    if (preferredName) storage.setItem(PREFERRED_NAME_KEY, preferredName);
    else storage.removeItem(PREFERRED_NAME_KEY);
  }

  function saveAvatarUrl(avatarUrl: string | null): void {
    if (avatarUrl) storage.setItem(AVATAR_URL_KEY, avatarUrl);
    else storage.removeItem(AVATAR_URL_KEY);
  }

  function saveHasBirthYear(hasBirthYear: boolean): void {
    storage.setItem(HAS_BIRTH_YEAR_KEY, hasBirthYear ? '1' : '0');
  }

  function saveIdentity(id: {
    playerId: string;
    guestToken: string;
    isGuest?: boolean;
    displayName?: string;
    hasBirthYear?: boolean;
    avatarUrl?: string | null;
    preferredName?: string | null;
  }): void {
    storage.setItem(PLAYER_ID_KEY, id.playerId);
    storage.setItem(GUEST_TOKEN_KEY, id.guestToken);
    if (id.isGuest === false) {
      storage.setItem(REGISTERED_KEY, '1');
      if (id.displayName) storage.setItem(DISPLAY_NAME_KEY, id.displayName);
      if (id.hasBirthYear !== undefined) saveHasBirthYear(id.hasBirthYear);
      if (id.avatarUrl !== undefined) saveAvatarUrl(id.avatarUrl);
      if (id.preferredName !== undefined) savePreferredName(id.preferredName);
    } else if (id.isGuest === true) {
      storage.removeItem(REGISTERED_KEY);
      storage.removeItem(DISPLAY_NAME_KEY);
      storage.removeItem(HAS_BIRTH_YEAR_KEY);
      storage.removeItem(AVATAR_URL_KEY);
      storage.removeItem(PREFERRED_NAME_KEY);
    }
  }

  function clearIdentity(): void {
    storage.removeItem(PLAYER_ID_KEY);
    storage.removeItem(GUEST_TOKEN_KEY);
    storage.removeItem(REGISTERED_KEY);
    storage.removeItem(DISPLAY_NAME_KEY);
    storage.removeItem(HAS_BIRTH_YEAR_KEY);
    storage.removeItem(AVATAR_URL_KEY);
    storage.removeItem(PREFERRED_NAME_KEY);
  }

  /**
   * A single in-flight guest mint, shared by concurrent callers. Without this, a burst
   * of first-use calls (e.g. the pack lobby's mount `listPacks` + an immediate
   * `uploadPack`) each see no persisted identity and mint their OWN guest, so one call's
   * upload and another's list end up scoped to DIFFERENT owners — the pack "vanishes"
   * from the list (#60). Sharing one promise makes them converge on a single identity.
   */
  let guestMintInFlight: Promise<Identity> | null = null;

  async function ensureGuestIdentity(forceFresh = false): Promise<Identity> {
    const existing = forceFresh ? null : loadIdentity();
    if (existing) return existing;
    // Ordinary concurrent callers share ONE in-flight mint so they can't create
    // competing guest identities (#60). A deliberate recovery mint (`forceFresh`)
    // bypasses the shared promise and always creates its own fresh identity.
    if (!forceFresh && guestMintInFlight) return guestMintInFlight;
    const mint = (async (): Promise<Identity> => {
      const res = await fetch(`${baseUrl}/api/auth/guest`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        // Carry the anonymous funnel key (#148 D148.4) so the server's `guest_created`
        // log line joins the anon `landed` head to this guest id.
        body: JSON.stringify({ fkey: getFunnelKey() }),
      });
      if (!res.ok) throw new Error(`could not create guest identity (${res.status})`);
      const body = (await res.json()) as { playerId: string; guestToken: string };
      saveIdentity(body);
      return { ...body, isGuest: true };
    })();
    if (forceFresh) return mint;
    guestMintInFlight = mint;
    try {
      return await mint;
    } finally {
      // Clear so a later call re-reads the now-persisted identity (or retries on failure).
      guestMintInFlight = null;
    }
  }

  async function recoverStaleIdentity(): Promise<Identity> {
    clearIdentity();
    return ensureGuestIdentity(true);
  }

  // ---- In-app Cognito auth (SRP) -----------------------------------------

  // One shared in-memory store for the whole auth flow — never localStorage.
  const cognitoStorage = new MemoryStorage();

  function userPool(cfg: CognitoClientConfig): CognitoUserPool {
    return new CognitoUserPool({
      UserPoolId: cfg.userPoolId,
      ClientId: cfg.clientId,
      Storage: cognitoStorage,
    });
  }

  function cognitoUser(cfg: CognitoClientConfig, username: string): CognitoUser {
    return new CognitoUser({
      Username: username,
      Pool: userPool(cfg),
      Storage: cognitoStorage,
    });
  }

  async function register(
    username: string,
    email: string,
    password: string,
    dateOfBirth: DateOfBirth,
  ): Promise<void> {
    const res = await fetch(`${baseUrl}/api/account/register`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        username,
        email,
        password,
        dateOfBirth,
        fkey: getFunnelKey(),
      }),
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
      };
      const err = new Error(
        body.error ?? `registration failed (${res.status})`,
      ) as Error & {
        code?: string;
      };
      // Surface the server's Cognito-style code so mapAuthError renders friendly copy.
      if (body.code) err.code = body.code;
      throw err;
    }
  }

  async function signIn(
    cfg: CognitoClientConfig,
    username: string,
    password: string,
  ): Promise<{ idToken: string }> {
    const authDetails = new AuthenticationDetails({
      Username: username,
      Password: password,
    });
    const user = cognitoUser(cfg, username);
    return new Promise((resolve, reject) => {
      user.authenticateUser(authDetails, {
        onSuccess: (session) => {
          const idToken = session.getIdToken().getJwtToken();
          user.signOut(); // clear the in-memory session; we keep only the ID token
          resolve({ idToken });
        },
        onFailure: (err) => reject(err),
      });
    });
  }

  async function forgotPassword(
    cfg: CognitoClientConfig,
    username: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser(cfg, username).forgotPassword({
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
        // Fired when a code is delivered (the common case) — treat as "code sent".
        inputVerificationCode: () => resolve(),
      });
    });
  }

  async function confirmReset(
    cfg: CognitoClientConfig,
    username: string,
    code: string,
    newPassword: string,
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      cognitoUser(cfg, username).confirmPassword(code, newPassword, {
        onSuccess: () => resolve(),
        onFailure: (err) => reject(err),
      });
    });
  }

  // ---- Guest → registered merge + profile mutations ----------------------

  async function claimAccount(idToken: string): Promise<Identity> {
    const guest = loadIdentity();
    const res = await fetch(`${baseUrl}/api/account/claim`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        idToken,
        ...(guest ? { guestToken: guest.guestToken } : {}),
        // Anonymous funnel key (#148 D148.4) so the `account_linked` log line joins the
        // anon head + guest id + new registered id into one funnel.
        fkey: getFunnelKey(),
      }),
    });
    if (!res.ok) throw new Error(`account claim failed (${res.status})`);
    const body = (await res.json()) as {
      playerId: string;
      guestToken: string;
      isGuest: boolean;
      displayName?: string;
      hasBirthYear?: boolean;
      avatarUrl?: string;
      preferredName?: string;
    };
    saveIdentity(body);
    return {
      playerId: body.playerId,
      guestToken: body.guestToken,
      isGuest: body.isGuest,
      ...(body.displayName ? { displayName: body.displayName } : {}),
      ...(body.hasBirthYear !== undefined ? { hasBirthYear: body.hasBirthYear } : {}),
      ...(body.avatarUrl ? { avatarUrl: body.avatarUrl } : {}),
      ...(body.preferredName ? { preferredName: body.preferredName } : {}),
    };
  }

  async function updateAvatar(
    dataBase64: string,
    contentType: string,
    guestToken: string,
  ): Promise<{ avatarUrl: string }> {
    const res = await fetch(`${baseUrl}/api/account/avatar`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ guestToken, contentType, dataBase64 }),
    });
    if (!res.ok) throw new Error(`could not update avatar (${res.status})`);
    const body = (await res.json()) as { ok: boolean; avatarUrl: string };
    saveAvatarUrl(body.avatarUrl);
    return { avatarUrl: body.avatarUrl };
  }

  async function clearAvatar(guestToken: string): Promise<void> {
    const res = await fetch(`${baseUrl}/api/account/avatar`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ guestToken, clear: true }),
    });
    if (!res.ok) throw new Error(`could not clear avatar (${res.status})`);
    saveAvatarUrl(null);
  }

  async function updateBirthYear(
    birthYear: number | null,
    guestToken: string,
  ): Promise<{ hasBirthYear: boolean }> {
    if (birthYear !== null && !isPlausibleBirthYear(birthYear)) {
      throw new Error('birthYear must be a plausible year');
    }
    const res = await fetch(`${baseUrl}/api/account/profile`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ birthYear, guestToken }),
    });
    if (!res.ok) throw new Error(`could not update profile (${res.status})`);
    const body = (await res.json()) as { ok: boolean; hasBirthYear: boolean };
    saveHasBirthYear(body.hasBirthYear);
    return { hasBirthYear: body.hasBirthYear };
  }

  async function updatePreferredName(
    name: string | null,
    guestToken: string,
  ): Promise<{ preferredName?: string }> {
    const trimmed = name === null ? null : name.trim();
    const preferredName = trimmed ? trimmed : null; // empty/whitespace → clear
    if (preferredName !== null && !isValidDisplayName(preferredName)) {
      throw new Error(`display name must be 1–${DISPLAY_NAME_MAX_LEN} characters`);
    }
    const res = await fetch(`${baseUrl}/api/account/profile`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ preferredName, guestToken }),
    });
    if (!res.ok) throw new Error(`could not update profile (${res.status})`);
    const body = (await res.json()) as { ok: boolean; preferredName?: string };
    savePreferredName(body.preferredName ?? null);
    return { ...(body.preferredName ? { preferredName: body.preferredName } : {}) };
  }

  async function acceptTerms(
    version: string,
    guestToken: string,
  ): Promise<{ tosAcceptedVersion: string; tosAcceptedEpochMs: number }> {
    const res = await fetch(`${baseUrl}/api/account/accept-terms`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ version, guestToken }),
    });
    if (!res.ok) throw new Error(`could not record consent (${res.status})`);
    const body = (await res.json()) as {
      tosAcceptedVersion: string;
      tosAcceptedEpochMs: number;
    };
    return {
      tosAcceptedVersion: body.tosAcceptedVersion,
      tosAcceptedEpochMs: body.tosAcceptedEpochMs,
    };
  }

  return {
    loadIdentity,
    saveIdentity,
    savePreferredName,
    saveAvatarUrl,
    saveHasBirthYear,
    ensureGuestIdentity,
    recoverStaleIdentity,
    register,
    signIn,
    forgotPassword,
    confirmReset,
    claimAccount,
    updateAvatar,
    clearAvatar,
    updateBirthYear,
    updatePreferredName,
    acceptTerms,
    clearIdentity,
  };
}
